import { SelectFinancingOffer } from './select-financing-offer';
import { IFinancingOfferFormatted } from '~/interfaces/synchrony/synchrony-interfaces';
import { Maybe } from 'graphql/jsutils/Maybe';
import {
  CashbackFinanceOffer,
  LowCostFinancingOffer,
  SynchronyConfiguration,
} from '~/gql/__generated__/types';
import { wmapStore } from '~/store/wmap-store';

// Match CB offer by plan months; fall back to first (default) offer for 6/12/24.
export function getCashbackFinanceOfferByMonths(
  cashbackFinanceOffers: Maybe<Maybe<CashbackFinanceOffer>[]> | undefined,
  offerMonths: number | null | undefined,
): CashbackFinanceOffer | undefined {
  if (!offerMonths || !cashbackFinanceOffers?.length) return undefined;

  const matched = cashbackFinanceOffers.find(
    (offer) => offer?.noInterestFor === offerMonths,
  );
  if (matched) return matched;

  return cashbackFinanceOffers[0] ?? undefined;
}

export const getLowCostOffer = (
  lowCostFinancingOffers: Maybe<Maybe<LowCostFinancingOffer>[]> | undefined,
  lowCostOfferName: string,
  numOfMonths?: number | null,
): IFinancingOfferFormatted | undefined => {
  const lowCostOffer: IFinancingOfferFormatted | undefined | null =
    lowCostFinancingOffers?.find(
      (item: LowCostFinancingOffer | undefined | null) =>
        item?.offerName?.trim()?.toLowerCase() ===
        lowCostOfferName?.trim()?.toLowerCase(),
    );
  if (lowCostOffer) {
    const { financingOfferDescription, ...rest } = lowCostOffer;
    // update the text from CMS to render dynamic num of months on checkout synchrony
    const updatedFinancingOfferDescription = financingOfferDescription?.replace(
      /{defaultNumOfMonths}/g,
      `${numOfMonths?.toString() || 6}`,
    );
    return {
      financingOfferDescription: updatedFinancingOfferDescription,
      ...rest,
    };
  }
};

export function extractOffersForCartAndCheckout(
  bspConfigs: SynchronyConfiguration,
  balanceAmount: number,
  returnCustomOfferArray: boolean | undefined,
) {
  const syfConfigs = bspConfigs.financingConfigurations;
  const syfOffersConfigs = bspConfigs.financingOffers;
  if (!syfOffersConfigs || !syfConfigs) return;
  return SelectFinancingOffer(
    {
      //   TODO:: IN STORE Offers - LIMITED
      //   limitedTimeFinancingOffers: syfOffersConfigs.limitedTimeFinancingOffer,
      everydayFinancingOffers: syfOffersConfigs.everydayFinanceOffer,
      totalPrice: balanceAmount,
      specialFinancingOffer: syfOffersConfigs.specialFinancingOffer,
      specialFinancingEnable: syfConfigs.specialFinancingEnable,
      cashBackFinancingOffer: syfOffersConfigs.cashbackFinanceOffers,
      cashbackFinancingEnable: syfConfigs.cashbackFinancingEnable,
      checkoutShowEveryDayOffers: syfConfigs.checkoutShowEveryDayOffers,
      lowCostFinancingOffers: syfOffersConfigs.lowCostFinancingOffer,
    },
    syfConfigs.lowCostOfferName,
    returnCustomOfferArray,
  );
}

export const getFinancingOfferDetails = (
  bspConfigs: SynchronyConfiguration,
  balanceAmount: number,
  returnCustomOfferArray?: boolean,
): IFinancingOfferFormatted | undefined => {
  const bestOffers = extractOffersForCartAndCheckout(
    bspConfigs,
    balanceAmount,
    returnCustomOfferArray,
  );
  return bestOffers?.bestSynchronyOffer;
};

export const everydayFinancingOffersForCheckout = (
  bspConfigs: SynchronyConfiguration,
  balanceAmount: number,
  returnCustomOfferArray?: boolean,
): IFinancingOfferFormatted[] => {
  const bestOffers = extractOffersForCartAndCheckout(
    bspConfigs,
    balanceAmount,
    returnCustomOfferArray,
  );
  return bestOffers?.everyDayOffersForCheckout || [];
};

export const monthlyAmount = (
  getOffer: IFinancingOfferFormatted | undefined,
  balanceAmount: number,
): number => {
  if (!getOffer) return 0;

  const syfDetails = wmapStore.getState().syfDetails;
  const updatedMonths = syfDetails?.planOfferKey?.split(',')[1] || '0';
  const numOfMonths = getOffer.numOfMonths || 0;
  let payMonthly = 0;

  if (balanceAmount && (+updatedMonths || numOfMonths)) {
    payMonthly = Math.ceil(balanceAmount / (+updatedMonths || numOfMonths));
  }

  //   TODO:: IN STORE Offers - LIMITED
  //   if (getOffer.isLTO) {
  //     payMonthly = getOffer.costPerMonth || 0;
  //   }

  return payMonthly;
};

export function isThreePercentPromoEligible(
  amountDue: number,
  minPurchase: number,
  enable3PercentOffers: number[] | undefined,
  offerMonths: number,
): boolean {
  return !!(
    amountDue >= minPurchase && enable3PercentOffers?.includes(offerMonths)
  );
}
